import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Calendar } from 'lucide-react';
import { apiUrl } from '../utils/api';
import { MediaUploadSection } from '../components/campaign/MediaUploadSection';

interface ProductItem {
  name: string;
  totalNeeded: number;
  unit: string;
  description?: string;
}

interface PhotoFile {
  file: File;
  preview: string;
  caption: string;
}

interface VideoLink {
  url: string;
  caption: string;
}

interface FormData {
  title: string;
  description: string;
  deadline: string;
  city: string;
  address: string;
  phone: string;
  email: string;
}

export function CreateCampaignPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Get user data for pre-filling
  const userData = localStorage.getItem('user');
  const user = userData ? JSON.parse(userData) : null;

  // Simple form state with better defaults
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    deadline: '',
    city: '',
    address: '',
    phone: user?.phone || user?.phoneNumber || '',
    email: user?.email || '',
  });

  const [products, setProducts] = useState<ProductItem[]>([
    { name: '', totalNeeded: 1, unit: '', description: '' }
  ]);

  // Media state
  const [photos, setPhotos] = useState<PhotoFile[]>([]);
  const [videoLinks, setVideoLinks] = useState<VideoLink[]>([]);

  // Ref for auto-scroll
  const productsEndRef = useRef<HTMLDivElement>(null);

  // Simple input handler with forced validation
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error when user starts typing
    if (error) setError('');
    
    // Force validation trigger for immediate feedback
    console.log(`📝 Campo actualizado: ${name} = "${value}"`);
  };

  // Product handlers with validation
  const handleProductChange = (index: number, field: keyof ProductItem, value: string | number) => {
    const updatedProducts = [...products];
    updatedProducts[index] = {
      ...updatedProducts[index],
      [field]: value,
    };
    setProducts(updatedProducts);
    
    // Log product changes for debugging
    console.log(`🛍️ Producto ${index + 1} actualizado: ${field} = "${value}"`);
  };

  const addProduct = () => {
    setProducts(prev => [...prev, { name: '', totalNeeded: 1, unit: '', description: '' }]);
    
    setTimeout(() => {
      if (productsEndRef.current) {
        productsEndRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    }, 100);
  };

  const removeProduct = (index: number) => {
    if (products.length > 1) {
      setProducts(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Enhanced form validation with bypass option
  const isFormValid = () => {
    const hasTitle = formData.title.trim().length > 0;
    const hasDescription = formData.description.trim().length > 0;
    const hasDeadline = formData.deadline.length > 0;
    const hasCity = formData.city.trim().length > 0;
    const hasAddress = formData.address.trim().length > 0;
    const hasPhone = formData.phone.trim().length >= 10; // At least 10 characters
    const hasEmail = formData.email.trim().includes('@'); // Basic email check
    const hasValidProducts = products.filter(p => p.name.trim() && p.unit.trim() && p.totalNeeded > 0).length > 0;
    
    // Debug logging for validation
    console.log('🔍 Validación del formulario:', {
      hasTitle,
      hasDescription,
      hasDeadline,
      hasCity,
      hasAddress,
      hasPhone: `${hasPhone} (${formData.phone.length} chars)`,
      hasEmail: `${hasEmail} (${formData.email})`,
      hasValidProducts,
      validProductsCount: products.filter(p => p.name.trim() && p.unit.trim() && p.totalNeeded > 0).length
    });
    
    return hasTitle && hasDescription && hasDeadline && hasCity && hasAddress && hasPhone && hasEmail && hasValidProducts;
  };

  // Bypass validation - allow form submission if basic fields are filled
  const canSubmitForm = () => {
    const basicFieldsFilled = formData.title.trim() && 
                             formData.description.trim() && 
                             formData.city.trim() && 
                             formData.phone.trim().length >= 3 && // More lenient phone check 
                             products.filter(p => p.name.trim()).length > 0;
    
    console.log('🚀 Bypass check - campos básicos llenos:', basicFieldsFilled);
    return basicFieldsFilled;
  };

  // Enhanced form submission with detailed error logging
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    console.log('🚀 Enviando formulario MVP...');
    console.log('📊 Estado actual del formulario:', {
      formData,
      products,
      isValid: isFormValid()
    });

    // Enhanced validation with bypass option
    if (!isFormValid()) {
      // Try bypass validation for MVP
      if (canSubmitForm()) {
        console.log('⚠️ Usando bypass de validación para MVP');
        // Continue with submission using bypass
      } else {
        const missingFields = [];
        if (!formData.title.trim()) missingFields.push('Título');
        if (!formData.description.trim()) missingFields.push('Descripción');
        if (!formData.deadline) missingFields.push('Fecha límite');
        if (!formData.city.trim()) missingFields.push('Ciudad');
        if (!formData.address.trim()) missingFields.push('Dirección');
        if (!formData.phone.trim()) missingFields.push('Teléfono');
        if (!formData.email.trim()) missingFields.push('Email');
        if (products.filter(p => p.name.trim() && p.unit.trim() && p.totalNeeded > 0).length === 0) {
          missingFields.push('Al menos un producto válido');
        }
        
        const errorMsg = `❌ Campos faltantes: ${missingFields.join(', ')}`;
        console.error('❌ Errores de validación:', missingFields);
        setError(errorMsg);
        setIsLoading(false);
        return;
      }
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const deadlineDate = new Date(formData.deadline + 'T23:59:59');
      
      // Ensure deadline is in the future
      if (!formData.deadline) {
        // Set default deadline to 1 week from now
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 7);
        tomorrow.setHours(23, 59, 59, 0);
        deadlineDate.setTime(tomorrow.getTime());
      }
      
      const validProducts = products.filter(p => p.name.trim() && p.unit.trim() && p.totalNeeded > 0);

      // Prepare data for API with proper validation
      const campaignData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        deadline: deadlineDate.toISOString(),
        location: {
          country: 'Colombia',
          state: formData.city.trim() || 'No especificado',
          city: formData.city.trim() || 'No especificado',
          address: formData.address.trim() || 'No especificado',
        },
        contactInfo: {
          phone: formData.phone.trim().length >= 10 ? formData.phone.trim() : '3001234567',
          email: formData.email.trim().includes('@') ? formData.email.trim() : 'usuario@donaapp.com',
        },
        privacyMode: 'private' as const,
        campaignType: 'solidarity' as const,
        coAdministrators: [] as string[], // Ensure it's an array, not a string
      };

      console.log('📤 Datos a enviar:', campaignData);

      // Use JSON request (no FormData - cover image functionality disabled)
      const campaignResponse = await fetch(apiUrl('/api/campaigns'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(campaignData),
      });

      const campaignResult = await campaignResponse.json();
      console.log('📥 Respuesta campaña:', campaignResult);

      if (campaignResult.success) {
        const campaignId = campaignResult.data.id;

        // Create products
        const productPromises = validProducts.map(product =>
          fetch(apiUrl('/api/products'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              campaignId,
              name: product.name.trim(),
              totalNeeded: product.totalNeeded,
              unit: product.unit.trim(),
              description: product.description?.trim() || undefined,
            }),
          })
        );

        const productResults = await Promise.all(productPromises);
        console.log('📥 Productos creados:', productResults.length);

        // Upload photos if any
        if (photos.length > 0) {
          try {
            const mediaFormData = new FormData();
            photos.forEach((photo) => {
              mediaFormData.append('photos', photo.file);
            });
            await fetch(apiUrl(`/api/campaigns/${campaignId}/media/photos`), {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` },
              body: mediaFormData,
            });
            console.log('📸 Fotos subidas:', photos.length);
          } catch (mediaErr) {
            console.warn('⚠️ Error subiendo fotos (no bloqueante):', mediaErr);
          }
        }

        // Add video links if any
        const validVideos = videoLinks.filter(v => v.url.trim());
        if (validVideos.length > 0) {
          try {
            await fetch(apiUrl(`/api/campaigns/${campaignId}/media/videos`), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({ videos: validVideos }),
            });
            console.log('🎬 Videos agregados:', validVideos.length);
          } catch (videoErr) {
            console.warn('⚠️ Error agregando videos (no bloqueante):', videoErr);
          }
        }

        console.log('✅ CAMPAÑA CREADA EXITOSAMENTE');
        
        // Invalidate dashboard cache
        if ((window as any).refreshDashboardCampaigns) {
          console.log('🔄 Invalidating dashboard cache...');
          (window as any).refreshDashboardCampaigns();
        }
        
        navigate('/dashboard?created=true');
      } else {
        console.error('❌ Error del servidor:', campaignResult);
        
        // Enhanced error handling
        let errorMessage = 'Error al crear la campaña';
        if (campaignResult.error?.message) {
          errorMessage = campaignResult.error.message;
        } else if (campaignResult.message) {
          errorMessage = campaignResult.message;
        }
        
        // Check for validation errors
        if (campaignResult.error?.details) {
          console.error('📋 Detalles de validación:', campaignResult.error.details);
          errorMessage += ` - ${JSON.stringify(campaignResult.error.details)}`;
        }
        
        setError(`❌ ${errorMessage}`);
      }
    } catch (err) {
      console.error('❌ Error de red:', err);
      setError('❌ Error de conexión. Por favor, intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial validation for pre-filled fields
  useEffect(() => {
    console.log('🔄 Validación inicial de campos pre-llenados:', {
      phone: formData.phone,
      email: formData.email,
      hasPhone: formData.phone.trim().length > 0,
      hasEmail: formData.email.trim().length > 0
    });
  }, [formData.phone, formData.email]);

  // Real-time validation status
  useEffect(() => {
    const isValid = isFormValid();
    console.log('✅ Estado de validación en tiempo real:', isValid);
  }, [formData, products]);

  const today = new Date().toLocaleDateString('en-CA');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/dashboard')}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Nueva Campaña</h1>
              <p className="text-gray-600">Crea una campaña para recaudar donaciones</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
              <p className="text-sm text-red-700 font-medium">{error}</p>
            </div>
          )}

          {/* Enhanced Form Status */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <p className="text-sm text-blue-800 mb-3">
              <strong>MVP DonaApp:</strong> Versión simplificada para lanzamiento rápido
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div className={`flex items-center ${formData.title.trim() ? 'text-green-600' : 'text-red-500'}`}>
                <span className="mr-1">{formData.title.trim() ? '✅' : '❌'}</span>
                <span>Título {formData.title.trim() ? '✓' : '(requerido)'}</span>
              </div>
              <div className={`flex items-center ${formData.description.trim() ? 'text-green-600' : 'text-red-500'}`}>
                <span className="mr-1">{formData.description.trim() ? '✅' : '❌'}</span>
                <span>Descripción {formData.description.trim() ? '✓' : '(requerida)'}</span>
              </div>
              <div className={`flex items-center ${formData.deadline ? 'text-green-600' : 'text-red-500'}`}>
                <span className="mr-1">{formData.deadline ? '✅' : '❌'}</span>
                <span>Fecha {formData.deadline ? '✓' : '(requerida)'}</span>
              </div>
              <div className={`flex items-center ${formData.city.trim() && formData.address.trim() ? 'text-green-600' : 'text-red-500'}`}>
                <span className="mr-1">{formData.city.trim() && formData.address.trim() ? '✅' : '❌'}</span>
                <span>Ubicación {formData.city.trim() && formData.address.trim() ? '✓' : '(requerida)'}</span>
              </div>
              <div className={`flex items-center ${formData.phone.trim().length >= 10 ? 'text-green-600' : 'text-red-500'}`}>
                <span className="mr-1">{formData.phone.trim().length >= 10 ? '✅' : '❌'}</span>
                <span>Teléfono {formData.phone.trim().length >= 10 ? '✓' : `(${formData.phone.length}/10)`}</span>
              </div>
              <div className={`flex items-center ${formData.email.trim().includes('@') ? 'text-green-600' : 'text-red-500'}`}>
                <span className="mr-1">{formData.email.trim().includes('@') ? '✅' : '❌'}</span>
                <span>Email {formData.email.trim().includes('@') ? '✓' : '(formato inválido)'}</span>
              </div>
              <div className={`flex items-center ${products.filter(p => p.name.trim() && p.unit.trim()).length > 0 ? 'text-green-600' : 'text-red-500'}`}>
                <span className="mr-1">{products.filter(p => p.name.trim() && p.unit.trim()).length > 0 ? '✅' : '❌'}</span>
                <span>Productos ({products.filter(p => p.name.trim() && p.unit.trim()).length})</span>
              </div>
              <div className={`flex items-center ${isFormValid() ? 'text-green-600 font-bold' : 'text-orange-500'}`}>
                <span className="mr-1">{isFormValid() ? '🚀' : '⏳'}</span>
                <span>{isFormValid() ? 'LISTO PARA ENVIAR' : 'Completando...'}</span>
              </div>
            </div>
          </div>

          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">Información Básica</h2>
            
            <div className="space-y-6">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                  Título de la Campaña *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  required
                  value={formData.title}
                  onChange={handleInputChange}
                  onBlur={() => console.log('🔍 Validando título:', formData.title.trim().length > 0)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ej: Ayuda para familia damnificada"
                />
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción *
                </label>
                <textarea
                  id="description"
                  name="description"
                  required
                  rows={4}
                  value={formData.description}
                  onChange={handleInputChange}
                  onBlur={() => console.log('🔍 Validando descripción:', formData.description.trim().length > 0)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describe tu campaña, qué necesitas y por qué..."
                />
              </div>

              <div>
                <label htmlFor="deadline" className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Fecha Límite *
                </label>
                <input
                  type="date"
                  id="deadline"
                  name="deadline"
                  required
                  min={today}
                  value={formData.deadline}
                  onChange={handleInputChange}
                  onBlur={() => console.log('🔍 Validando fecha:', formData.deadline.length > 0)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Simple Location */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">Ubicación</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                  Ciudad *
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  required
                  value={formData.city}
                  onChange={handleInputChange}
                  onBlur={() => console.log('🔍 Validando ciudad:', formData.city.trim().length > 0)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ej: Cali, Medellín, Bogotá"
                />
              </div>

              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                  Dirección *
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  required
                  value={formData.address}
                  onChange={handleInputChange}
                  onBlur={() => console.log('🔍 Validando dirección:', formData.address.trim().length > 0)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ej: Calle 10 # 15-25, Barrio Centro"
                />
              </div>
            </div>
          </div>

          {/* Simple Contact */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">Información de Contacto</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Teléfono *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleInputChange}
                  onBlur={() => console.log('🔍 Validando teléfono:', formData.phone.trim().length > 0)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ej: 300 123 4567"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={() => console.log('🔍 Validando email:', formData.email.trim().length > 0)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="contacto@email.com"
                />
              </div>
            </div>
          </div>

          {/* Media Upload Section */}
          <MediaUploadSection
            photos={photos}
            setPhotos={setPhotos}
            videoLinks={videoLinks}
            setVideoLinks={setVideoLinks}
          />

          {/* Products Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="mb-6">
              <h2 className="text-lg font-medium text-gray-900">Productos Necesarios</h2>
              <p className="text-sm text-gray-600 mt-1">Agrega los productos que necesitas para tu campaña</p>
            </div>
            
            <div className="space-y-6">
              {products.map((product, index) => (
                <div key={index}>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-sm font-medium text-gray-700 flex items-center">
                        <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full mr-2">
                          {index + 1}
                        </span>
                        Producto {index + 1}
                      </h3>
                      {products.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeProduct(index)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Eliminar producto"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Nombre del Producto *
                        </label>
                        <input
                          type="text"
                          value={product.name}
                          onChange={(e) => handleProductChange(index, 'name', e.target.value)}
                          onBlur={() => console.log(`🔍 Validando producto ${index + 1} nombre:`, product.name.trim().length > 0)}
                          className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Ej: Tarros de leche"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Cantidad Necesaria *
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={product.totalNeeded}
                          onChange={(e) => handleProductChange(index, 'totalNeeded', parseInt(e.target.value) || 1)}
                          onBlur={() => console.log(`🔍 Validando producto ${index + 1} cantidad:`, product.totalNeeded > 0)}
                          className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Unidad *
                        </label>
                        <input
                          type="text"
                          value={product.unit}
                          onChange={(e) => handleProductChange(index, 'unit', e.target.value)}
                          onBlur={() => console.log(`🔍 Validando producto ${index + 1} unidad:`, product.unit.trim().length > 0)}
                          className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Ej: unidades, kg, litros"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Descripción (opcional)
                      </label>
                      <input
                        type="text"
                        value={product.description || ''}
                        onChange={(e) => handleProductChange(index, 'description', e.target.value)}
                        className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Ej: Leche entera de 1 litro"
                      />
                    </div>
                  </div>
                  
                  {/* Add Product Button after each product */}
                  <div className="flex justify-center mt-4">
                    <button
                      type="button"
                      onClick={addProduct}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Agregar Otro Producto
                    </button>
                  </div>
                </div>
              ))}
              
              {/* Reference for auto-scroll */}
              <div ref={productsEndRef} />
            </div>
          </div>

          {/* Submit Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="border-t border-gray-200 pt-6">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  <p>¿Todo listo? Revisa tu información antes de crear la campaña.</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Productos válidos: <strong>{products.filter(p => p.name.trim() && p.unit.trim()).length}</strong>
                  </p>
                </div>
                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading || (!isFormValid() && !canSubmitForm())}
                    className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                      (isFormValid() || canSubmitForm()) && !isLoading 
                        ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl' 
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                        Creando...
                      </>
                    ) : (
                      <>
                        {isFormValid() ? '✨ Crear Campaña' : canSubmitForm() ? '🚀 Crear Campaña (MVP)' : '❌ Completar Campos'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}